"use client"

/**
 * Guided Finance onboarding — the wizard's per-branch progress (UX only).
 *
 * This lets the guided Configure Finance flow resume exactly where the user left off, across devices. It is a
 * pure UX concern: it NEVER decides whether finance is unlocked. That decision is derived from real accounting
 * data via {@link isMinimumSetupComplete}. The intro is not a step — a `null` progress means "not started".
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { financeQueryKeys } from "@/features/finance/services/finance-query-keys"

import { http } from "@/shared/lib/api/http"

/** The wizard's steps, in flow order. Mirrors the backend `FinanceOnboardingStep` enum. */
export enum FinanceOnboardingStep {
  Accounting = "ACCOUNTING",
  /**
   * ADR-0024 — how the organization runs its books. Shown to the PARISH of a multi-branch workspace only,
   * and placed second on purpose: governance decides whether the accounts built by the steps after it are
   * global or branch-owned, so choosing it at the end would mean choosing it too late.
   */
  Governance = "GOVERNANCE",
  Accounts = "ACCOUNTS",
  Funds = "FUNDS",
  Categories = "CATEGORIES",
  /** IBA-170 — the final, mandatory step: divide the opening money across funds, then open the books. */
  FundAllocation = "FUND_ALLOCATION",
}

/** Wizard lifecycle. `Finished` = completed or dismissed; a UX marker only. Mirrors backend enum. */
export enum FinanceOnboardingStatus {
  InProgress = "IN_PROGRESS",
  Finished = "FINISHED",
}

export type FinanceOnboardingProgress = {
  currentStep: FinanceOnboardingStep
  status: FinanceOnboardingStatus
  updatedAt: string
}

export type SaveFinanceOnboardingProgressInput = {
  currentStep: FinanceOnboardingStep
  status: FinanceOnboardingStatus
}

export const financeOnboardingKey = (workspaceId: string | null, branchId: string | null) =>
  financeQueryKeys.onboardingProgress(workspaceId, branchId)

export async function fetchFinanceOnboardingProgress(
  workspaceId: string,
  branchId: string,
): Promise<FinanceOnboardingProgress | null> {
  const response = await http.get<FinanceOnboardingProgress | null>("/finance/onboarding-progress", {
    params: { workspaceId, branchId },
  })
  return response.data ?? null
}

export async function saveFinanceOnboardingProgress(
  workspaceId: string,
  branchId: string,
  input: SaveFinanceOnboardingProgressInput,
): Promise<FinanceOnboardingProgress> {
  const response = await http.put<FinanceOnboardingProgress>("/finance/onboarding-progress", {
    workspaceId,
    branchId,
    ...input,
  })
  return response.data
}

export function useFinanceOnboardingProgressQuery(workspaceId: string | null, branchId: string | null) {
  return useQuery({
    queryKey: financeOnboardingKey(workspaceId, branchId),
    queryFn: () => fetchFinanceOnboardingProgress(workspaceId as string, branchId as string),
    enabled: Boolean(workspaceId && branchId),
  })
}

export function useUpdateFinanceOnboardingProgressMutation(
  workspaceId: string | null,
  branchId: string | null,
) {
  const queryClient = useQueryClient()
  const queryKey = financeOnboardingKey(workspaceId, branchId)

  return useMutation({
    mutationFn: (input: SaveFinanceOnboardingProgressInput) =>
      saveFinanceOnboardingProgress(workspaceId as string, branchId as string, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<FinanceOnboardingProgress | null>(queryKey)
      const optimistic: FinanceOnboardingProgress = {
        currentStep: input.currentStep,
        status: input.status,
        updatedAt: previous?.updatedAt ?? new Date().toISOString(),
      }
      queryClient.setQueryData(queryKey, optimistic)
      return { previous }
    },
    onError: (_error, _input, context) => {
      queryClient.setQueryData(queryKey, context?.previous ?? null)
    },
    onSuccess: (progress) => {
      queryClient.setQueryData(queryKey, progress)
    },
  })
}
